import React, { useState } from 'react'
import styled from 'styled-components'
import { MarginProps, margin } from 'styled-system'
import { startCase } from 'lodash-es'

import Avatar from 'components/Avatar'
import Icon from 'components/Icon'
import PopOver from 'components/PopOver'
import { PopOverItem } from 'components/PopOver/types'
import Spinner from 'components/Spinner'
import Text from 'components/Text'

type DropDownProps = MarginProps & {
  active?: boolean
  firstName: string
  lastName: string
  onSelect?: (value: string) => void
  options: PopOverItem[]
  showName?: boolean
  subText?: string
  imgSrc?: string
  isLoadingAvatar?: boolean
  avatarPlaceHolder?: string
  customMenuList?: React.ReactNode
}

const ProfileDropdown = ({
  active = false,
  firstName,
  lastName,
  onSelect,
  options,
  subText = 'Customer Profile',
  showName = true,
  imgSrc,
  isLoadingAvatar,
  avatarPlaceHolder,
  customMenuList,
  ...props
}: DropDownProps) => {
  const [openMenu, setOpenMenu] = useState(false)

  const handleSelect = (value?: PopOverItem) => {
    if (!value) return

    onSelect?.(value?.value)
    setOpenMenu(false)
  }

  const component = (
    <MainContainer active={active} {...props}>
      <Container active={openMenu} onClick={() => setOpenMenu(!openMenu)} showName={showName}>
        {isLoadingAvatar ? (
          <LoadingIconWrapper>
            <Spinner />
          </LoadingIconWrapper>
        ) : (
          <Avatar size="small" inline imgSrc={imgSrc} placeholder={avatarPlaceHolder} />
        )}
        {showName && (
          <NameContainer>
            <Text color={openMenu ? 'white' : 'defaultText'} fontSize={12} mb="4px">
              {subText}
            </Text>
            <Name active={openMenu}>{`${startCase(firstName)} ${startCase(lastName)}`}</Name>
          </NameContainer>
        )}
        {options.length > 0 && (
          <Icon icon={['far', 'angle-down']} color="gray4" {...(openMenu && { flip: 'vertical' })} />
        )}
      </Container>
    </MainContainer>
  )

  return options.length > 0 ? (
    <PopOver
      isPortaled={true}
      onChange={handleSelect}
      onToggleMenu={setOpenMenu}
      options={options}
      placement="bottom-end"
      width={showName ? 216 : 94}
      customMenuList={customMenuList}
    >
      {component}
    </PopOver>
  ) : (
    component
  )
}

const MainContainer = styled.div<MarginProps & { active?: boolean }>`
  position: relative;
  ${margin}
`

const Container = styled.div<{ active?: boolean; showName?: boolean }>`
  align-items: center;
  background-color: ${(p) => (p.active ? p.theme.colors.gray5 : '#eee')};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  height: 50px;
  justify-content: space-evenly;
  width: ${(p) => (p.showName ? 216 : 94)}px;
  user-select: none;
`
const NameContainer = styled.div`
  width: 50%;
`

const Name = styled.div<{ active: boolean }>`
  ${(p) => p.theme.font({ family: 'roboto-condensed', weight: 'bold', color: p.active ? 'white' : 'defaultText' })};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const LoadingIconWrapper = styled.span`
  svg {
    color: ${(p) => p.theme.colors.black};
  }
`

export default ProfileDropdown
